///
/// EarthBrowser
///
/// Created by Matt Giger on 3/24/2024
/// Copyright (c) 2024 Geodata Labs
///

/**
 * Represents a spring system for smooth value transitions.
 */
export class spring {
	/** Source values for the spring system */
	private source: number[] = []
	/** Callback function to be called on each update */
	private updateCallback?: (current: number[]) => void
	/** Callback function to be called when the spring system completes */
	private completeCallback?: () => void
	/** Flag indicating whether the spring system is currently running */
	private _running: boolean = false
	/** Tolerance values for determining when the spring has settled */
	private _tolerance: number[] = [1.0, 0.01, 0.01]
	/** Damping constant for the spring system */
	private damping_k: number = 0
	/** Spring constant for the spring system */
	private spring_k: number = 0
	/** Maximum time delta for each update step */
	private max_delta: number = 1
	/** Array of spring_value objects representing each property in the system */
	private props: spring_value[] = []
	/** ID of the current animation frame */
	private frame_id: number = 0

	/**
	 * Stops the spring system if it's currently running.
	 */
	public stop(): void {
		if(this._running) {
			cancelAnimationFrame(this.frame_id)
			this._running = false
		}
	}

	/**
	 * Clears all properties and stops the spring system.
	 */
	public clear(): void {
		this.props = []
		this.stop()
	}

	/**
	 * Gets the current running state of the spring system.
	 * @returns {boolean} True if the spring system is running, false otherwise.
	 */
	public get running(): boolean {
		return this._running
	}

	/**
	 * Gets the current values of the spring system.
	 * If the spring is running, returns the current animated values.
	 * If the spring is not running, returns the source values.
	 * @returns {number[]} The current values.
	 */
	public get current(): number[] {
		if(this.props.length > 0) {
			return this.props.map(prop => prop.current)
		}
		return this.source
	}

	/**
	 * Sets the source values for the spring system.
	 * @param {number[]} source - The source values to set.
	 * @returns {spring} The current spring instance for method chaining.
	 */
	public from(source: number[]): spring {
		this.source = source
		return this
	}

	/**
	 * Sets the damping value for the spring system.
	 * @param {number} value - The damping value to set.
	 * @returns {spring} The current spring instance for method chaining.
	 */
	public damping(value: number): spring {
		if(value != this.damping_k) {
			this.damping_k = value
			this.spring_k = this.damping_k * this.damping_k /4.0
			this.max_delta = Math.sqrt(0.353 / this.spring_k)

			let idx = 0
			for(const prop of this.props) {
				const tolerance = this._tolerance[idx++ % this._tolerance.length]
				prop.set_constants(this.spring_k, this.damping_k, tolerance, this.max_delta)
			}
		}
		return this
	}

	/**
	 * Sets the tolerance values for the spring system.
	 * @param {number[]} value - The tolerance values to set.
	 * @returns {spring} The current spring instance for method chaining.
	 */
	public tolerance(value: number[]): spring {
		this._tolerance = value
		return this
	}

	/**
	 * Sets the update callback function.
	 * @param {(current: number[]) => void} callback - The callback function to be called on each update.
	 * @returns {spring} The current spring instance for method chaining.
	 */
	public onUpdate(callback: (current: number[]) => void): spring {
		this.updateCallback = callback
		return this
	}

	/**
	 * Sets the complete callback function.
	 * @param {() => void} callback - The callback function to be called when the spring system completes.
	 * @returns {spring} The current spring instance for method chaining.
	 */
	public onComplete(callback: () => void): spring {
		this.completeCallback = callback
		return this
	}

	/**
	 * Initiates the spring system to transition to the specified destination values.
	 * @param {number[]} destination - The destination values for the spring system.
	 */
	public sendTo(destination: number[]) {
		if(this._running) {
			cancelAnimationFrame(this.frame_id)
		}

		if(this.source.length !== destination.length) {
			throw new Error("Source and destination must be the same length")
		}

		const time = performance.now() * 0.001 - 0.01
		if(this.props.length === 0) {
			for(let i = 0; i < this.source.length; i++) {
				const prop = new spring_value(this.source[i], destination[i])
				if(!this._running) {
					prop.time = time
				}
				const tolerance = this._tolerance[i % this._tolerance.length]
				prop.set_constants(this.spring_k, this.damping_k, tolerance, this.max_delta)
				this.props.push(prop)
			}
		} else {
			for(let i = 0; i < this.props.length; i++) {
				const prop = this.props[i]
				if(!this._running) {
					prop.time = time
				}

				const tolerance = this._tolerance[i % this._tolerance.length]
				prop.set_constants(this.spring_k, this.damping_k, tolerance, this.max_delta)
				prop.current = this.source[i]
				prop.destination = destination[i]
			}
		}

		this.frame_id = requestAnimationFrame(this.frame.bind(this))
		this._running = true
	}

	/**
	 * Updates the spring system for each animation frame.
	 * @param {DOMHighResTimeStamp} timestamp - The current timestamp of the animation frame.
	 */
	private frame(timestamp: DOMHighResTimeStamp): void {
		if(this._running) {

			let withinTolerance = true
			for(const prop of this.props) {
				if(!prop.update(timestamp * 0.001)) {
					withinTolerance = false
				}
			}
			this.updateCallback?.(this.props.map(prop => prop.current))

			if(!withinTolerance) {
				this.frame_id = requestAnimationFrame(this.frame.bind(this))
			} else {
				this._running = false
				this.completeCallback?.()
			}
		}
	}
}

/**
 * Represents a single value in the spring system.
 * This class handles the calculations for updating a spring-based value over time.
 */
class spring_value {
	/** Longest gap (seconds) one update will simulate; see update() */
	private static readonly max_catch_up = 1
	public current: number
	public destination: number
	public time: number = 0
	public velocity: number = 0
	public spring_k: number = 0
	public damping_k: number = 0
	public tolerance: number = 0
	public max_delta: number = 0

	constructor(start: number, destination: number) {
		this.current = start
		this.destination = destination
	}

	public set_constants(spring_k: number, damping_k: number, tolerance: number, max_delta: number): void {
		this.spring_k = spring_k
		this.damping_k = damping_k
		this.tolerance = tolerance
		this.max_delta = max_delta
	}

	public update(time: number): boolean {
		// A stall (background tab, app switch, long GC) shows up as a large
		// gap; integrate at most this much of it so resume is one bounded
		// step that continues the motion, not a frame-long catch-up loop
		if(time - this.time > spring_value.max_catch_up) {
			this.time = time - spring_value.max_catch_up
		}
		let displace = this.destination - this.current
		while(time > this.time) {
			const tdelta = Math.min(time - this.time, this.max_delta)
			const spr = displace * this.spring_k
			const dmp = this.velocity * this.damping_k
			const accel = spr - dmp
			this.velocity += accel * tdelta
			this.current += this.velocity * tdelta
			displace = this.destination - this.current
			this.time += tdelta
		}
		this.time = time;

		return Math.abs(this.destination - this.current) <= this.tolerance
	}
}
